import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { mapInvoiceIntegrationModulesToRows } from "./useTenantIntegrationModulesForInvoices";

import { integrationsApi } from "@/features/integrations/api/integrations.api";

type UseIntegrationModuleForInvoicesParams = {
  tenantIntegrationModuleId: string;
  tenantId?: string | null;
};

/**
 * Resolves integration module display name for the given tenantIntegrationModuleId.
 * Uses a separate wide lookup query (not the paginated list) so the module is found regardless of list page.
 */
export function useIntegrationModuleForInvoices(
  params: UseIntegrationModuleForInvoicesParams
) {
  const { data } = useQuery({
    queryKey: [
      "integrationModules",
      "invoices",
      "lookup",
      params.tenantId,
      params.tenantIntegrationModuleId,
    ],
    queryFn: () =>
      integrationsApi.fetchCampaignInvoiceIntegrations({
        tenantId: params.tenantId || undefined,
        page: 1,
        pageSize: 500,
      }),
    enabled: !!params.tenantIntegrationModuleId,
    staleTime: 5 * 60 * 1000,
  });

  const modules = useMemo(
    () => mapInvoiceIntegrationModulesToRows(data?.data),
    [data?.data]
  );

  const module = useMemo(
    () =>
      modules.find(
        (m) => m.tenantIntegrationModuleId === params.tenantIntegrationModuleId
      ),
    [modules, params.tenantIntegrationModuleId]
  );

  return {
    data: module
      ? { name: module.name, integrationName: module.integrationName }
      : undefined,
  };
}
